<template>
  <b-card class="mb-4 program-status-card">
    <b-overlay :show="loading">
      <div class="d-flex flex-wrap justify-content-between align-items-center">
        <div class="d-flex align-items-center mb-2 mb-md-0">
          <span class="font-weight-bold mr-2">Tình trạng chương trình:</span>
          <span class="status-badge" :class="statusMeta.className">{{ statusMeta.label }}</span>
          <span v-if="program && program.result && program.evaluatedAt" class="text-muted small ml-2">
            Đánh giá lúc {{ formatDateTime(program.evaluatedAt) }}
          </span>
          <span v-else-if="program && program.status !== 0 && program.statusChangedAt" class="text-muted small ml-2">
            Cập nhật lúc {{ formatDateTime(program.statusChangedAt) }}
          </span>
        </div>
        <div v-if="isUpdatePermission && program" class="d-flex flex-wrap">
          <b-button
            v-if="canPause"
            size="sm"
            variant="outline-warning"
            class="mr-2 mb-1"
            @click="openAction('status', 1)"
          >
            <i class="fas fa-pause mr-1"></i>Tạm dừng
          </b-button>
          <b-button
            v-if="canResume"
            size="sm"
            variant="outline-primary"
            class="mr-2 mb-1"
            @click="openAction('status', 0)"
          >
            <i class="fas fa-play mr-1"></i>Tiếp tục
          </b-button>
          <b-button
            v-if="canQuit"
            size="sm"
            variant="outline-secondary"
            class="mr-2 mb-1"
            @click="openAction('status', 2)"
          >
            <i class="fas fa-ban mr-1"></i>Từ bỏ
          </b-button>
          <template v-if="canEvaluateButtons">
            <b-button
              size="sm"
              variant="outline-success"
              class="mr-2 mb-1"
              :disabled="!program.canEvaluate"
              :title="program.canEvaluate ? '' : 'Chỉ đánh giá khi khách hàng đã kết thúc lộ trình'"
              @click="openAction('evaluate', 1)"
            >
              <i class="fas fa-check mr-1"></i>Đạt
            </b-button>
            <b-button
              size="sm"
              variant="outline-danger"
              class="mr-2 mb-1"
              :disabled="!program.canEvaluate"
              :title="program.canEvaluate ? '' : 'Chỉ đánh giá khi khách hàng đã kết thúc lộ trình'"
              @click="openAction('evaluate', 2)"
            >
              <i class="fas fa-times mr-1"></i>Không đạt
            </b-button>
          </template>
          <b-button size="sm" variant="link" class="mb-1" @click="toggleLogs">
            <i class="fas fa-history mr-1"></i>Lịch sử
          </b-button>
        </div>
      </div>
      <div v-if="noteText" class="text-muted small mt-2">Ghi chú: {{ noteText }}</div>

      <b-collapse v-model="showLogs">
        <div class="mt-3">
          <div v-for="log in logs" :key="log.id" class="d-flex justify-content-between border-top py-2 small">
            <div>
              <span class="font-weight-bold">{{ actionLabel(log.action) }}</span>
              <span v-if="log.note" class="text-muted"> — {{ log.note }}</span>
            </div>
            <div class="text-muted text-right">
              {{ log.creatorName || '' }}<span v-if="log.createDatetime"> · {{ formatDateTime(log.createDatetime) }}</span>
            </div>
          </div>
          <div v-if="!logs.length" class="text-muted small border-top pt-2">Chưa có lịch sử thay đổi.</div>
        </div>
      </b-collapse>
    </b-overlay>

    <b-modal
      v-model="showModal"
      :title="modalTitle"
      ok-title="Xác nhận"
      cancel-title="Huỷ"
      :ok-disabled="submitting"
      @ok.prevent="submitAction"
    >
      <p class="mb-2">{{ modalMessage }}</p>
      <b-form-textarea v-model="modalNote" rows="3" placeholder="Ghi chú (không bắt buộc)" />
    </b-modal>
  </b-card>
</template>

<script>
import { getProgramStatus, getProgramLogs, updateProgramStatus, evaluateProgram } from '../services/services';

const STATUS_META = {
  inProgress: { label: 'Đang cập nhật', className: 'status-in-progress' },
  paused: { label: 'Tạm dừng', className: 'status-paused' },
  quit: { label: 'Từ bỏ', className: 'status-quit' },
  passed: { label: 'Đạt', className: 'status-passed' },
  failed: { label: 'Không đạt', className: 'status-failed' },
};

const ACTION_LABELS = {
  1: 'Tạm dừng',
  2: 'Tiếp tục',
  3: 'Từ bỏ',
  4: 'Đánh giá Đạt',
  5: 'Đánh giá Không đạt',
};

export default {
  name: 'ProgramStatusBar',
  props: {
    userPackageId: { type: String, default: null },
    isUpdatePermission: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      submitting: false,
      program: null,
      logs: [],
      showLogs: false,
      showModal: false,
      modalType: null,
      modalValue: null,
      modalNote: '',
    };
  },
  computed: {
    statusMeta() {
      const p = this.program;
      if (!p) return STATUS_META.inProgress;
      if (p.result === 1) return STATUS_META.passed;
      if (p.result === 2) return STATUS_META.failed;
      if (p.status === 1) return STATUS_META.paused;
      if (p.status === 2) return STATUS_META.quit;
      return STATUS_META.inProgress;
    },
    isLocked() {
      return !this.program || !!this.program.result || this.program.status === 2;
    },
    canPause() {
      return !this.isLocked && this.program.status === 0;
    },
    canResume() {
      return !this.isLocked && this.program.status === 1;
    },
    canQuit() {
      return !this.isLocked;
    },
    canEvaluateButtons() {
      return this.program && this.program.status !== 2;
    },
    noteText() {
      const p = this.program;
      if (!p) return '';
      return p.result ? p.resultNote : p.statusNote;
    },
    modalTitle() {
      if (this.modalType === 'evaluate') return this.modalValue === 1 ? 'Đánh giá Đạt' : 'Đánh giá Không đạt';
      return { 0: 'Tiếp tục chương trình', 1: 'Tạm dừng chương trình', 2: 'Từ bỏ chương trình' }[this.modalValue];
    },
    modalMessage() {
      if (this.modalType === 'evaluate') {
        return `Xác nhận khách hàng ${this.modalValue === 1 ? 'ĐẠT' : 'KHÔNG ĐẠT'} mục tiêu sau khi kết thúc lộ trình?`;
      }
      if (this.modalValue === 2) return 'Khách hàng ngưng tham gia và không quay lại chương trình. Sau khi từ bỏ sẽ không đổi lại trạng thái được.';
      if (this.modalValue === 1) return 'Khách hàng tạm hoãn tham gia, có thể tiếp tục lại sau.';
      return 'Khách hàng quay lại tiếp tục tham gia chương trình.';
    },
  },
  watch: {
    userPackageId: {
      immediate: true,
      handler() {
        this.loadProgram();
      },
    },
  },
  methods: {
    async loadProgram() {
      if (!this.userPackageId) return;
      this.loading = true;
      try {
        const res = await getProgramStatus(this.userPackageId);
        this.program = res?.data ?? res;
        if (this.showLogs) await this.loadLogs();
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không thể tải tình trạng chương trình.' });
      } finally {
        this.loading = false;
      }
    },
    async loadLogs() {
      const res = await getProgramLogs(this.userPackageId);
      this.logs = res?.data ?? res ?? [];
    },
    async toggleLogs() {
      this.showLogs = !this.showLogs;
      if (this.showLogs) {
        try {
          await this.loadLogs();
        } catch (e) {
          this.$toastr.e({ title: 'Lỗi', msg: 'Không thể tải lịch sử.' });
        }
      }
    },
    openAction(type, value) {
      this.modalType = type;
      this.modalValue = value;
      this.modalNote = '';
      this.showModal = true;
    },
    async submitAction() {
      this.submitting = true;
      try {
        const payload = { note: this.modalNote || null };
        const res = this.modalType === 'evaluate'
          ? await evaluateProgram(this.userPackageId, { ...payload, result: this.modalValue })
          : await updateProgramStatus(this.userPackageId, { ...payload, status: this.modalValue });
        this.program = res?.data ?? res;
        this.showModal = false;
        this.$toastr.s({ title: 'Thành công', msg: 'Đã cập nhật tình trạng chương trình.' });
        if (this.showLogs) await this.loadLogs();
        this.$emit('changed', this.program);
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Không thể cập nhật tình trạng.';
        this.$toastr.e({ title: 'Lỗi', msg });
      } finally {
        this.submitting = false;
      }
    },
    actionLabel(action) {
      return ACTION_LABELS[action] || '';
    },
    formatDateTime(value) {
      if (!value) return '';
      const raw = String(value);
      let date;
      if (/^\d+$/.test(raw)) date = new Date(Number(raw) * 1000);
      else date = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : `${raw}Z`);
      return isNaN(date.getTime()) ? '' : date.toLocaleString('vi-VN');
    },
  },
};
</script>

<style scoped>
.program-status-card {
  border: 1px solid #EBEDF3;
  border-radius: 10px;
}

.status-badge {
  display: inline-block;
  padding: 3px 14px;
  border-radius: 20px;
  border: 1px solid;
  font-size: 13px;
  font-weight: 600;
}

.status-in-progress {
  color: #1E5BB8;
  background: #EAF2FF;
  border-color: #6C9EEB;
}

.status-paused {
  color: #A15C00;
  background: #FFF4DE;
  border-color: #F0B44C;
}

.status-quit {
  color: #5E6278;
  background: #F3F6F9;
  border-color: #B5B5C3;
}

.status-passed {
  color: #1B7A3A;
  background: #E8FFF3;
  border-color: #50CD89;
}

.status-failed {
  color: #B42318;
  background: #FFF5F8;
  border-color: #F1416C;
}
</style>
